import { captureGenericError } from 'src/lib/sentry';
import type { ExternalFriend } from 'src/replicant/friends/friends.ruleset';
import { friendListLimit } from 'src/replicant/friends/friends.ruleset';
import StateObserver from 'src/StateObserver';

// Store promises for individual user profiles
const profilePromises: Record<string, Promise<ExternalFriend | undefined>> = {};

/**
 * Get players profiles by player IDs using promise-based caching
 *
 * This function stores individual promises for each user ID to prevent duplicate API calls.
 * Multiple concurrent requests for the same user ID will share the same promise.
 * Each profile contains the player's ID, name, and profile picture.
 * The profiles are sorted by ID to maintain a consistent order.
 *
 * @param {string[]} playersIds - An array of player IDs to fetch profiles for.
 * @returns {Promise<ExternalFriend[]>} - A promise that resolves to an array of player profiles.
 */
export async function getPlayersProfiles(
  playersIds: string[],
): Promise<ExternalFriend[]> {
  // Remove duplicates from input
  const uniquePlayerIds = [...new Set(playersIds)];

  // Filter out user IDs that already have ongoing promises
  const unfetchedPlayerIds = uniquePlayerIds.filter(
    (userId) => profilePromises[userId] === undefined,
  );

  // `fetchPlayersProfiles` returns at most `friendListLimit` profiles, so sending
  // more IDs than that only inflates the Replicant request and the OpenSearch
  // terms query without returning more profiles. IDs beyond the limit are left
  // uncached and are picked up by a subsequent call, so callers should pass IDs
  // in priority order — friend lists come in newest-first via
  // `getBuruFriendsByRecency`.
  const requestedPlayerIds = unfetchedPlayerIds.slice(0, friendListLimit);

  // Track if we've already logged an error for this function call
  let hasLoggedError = false;

  // If there are un fetched user IDs, create promises for them
  if (requestedPlayerIds.length > 0) {
    try {
      // Make a single API call for all un fetched user IDs
      const fetchProfilesPromise =
        StateObserver.replicant.asyncGetters.fetchPlayersProfiles({
          ids: requestedPlayerIds,
          locale: StateObserver.getLocale(),
        });

      // Create individual promises for each unfetched user ID
      for (const unfetchedUserId of requestedPlayerIds) {
        profilePromises[unfetchedUserId] = fetchProfilesPromise
          .then((profiles: ExternalFriend[]) => {
            // Find the profile for this specific user ID
            const profile = profiles.find((p) => p.id === unfetchedUserId);

            if (!profile) {
              // Clean up promise if profile not found
              delete profilePromises[unfetchedUserId];
              return undefined;
            }

            return profile;
          })
          .catch((error: unknown) => {
            // Log error only once per function call to avoid spam
            if (!hasLoggedError) {
              hasLoggedError = true;
              captureGenericError(
                `Failed to fetch player profiles for batch`,
                error,
              );
            }

            // Always clean up promise regardless of logging
            delete profilePromises[unfetchedUserId];
            return undefined;
          });
      }
    } catch (error) {
      // Handle immediate errors (shouldn't happen with promises, but safety first)
      if (!hasLoggedError) {
        hasLoggedError = true;
        captureGenericError('Failed to initiate profile fetch', error);
      }
    }
  }

  // Wait for all promises and collect successful profiles
  const profiles: ExternalFriend[] = [];

  await Promise.all(
    uniquePlayerIds.map(async (userId) => {
      const profile = await profilePromises[userId];
      if (profile) {
        profiles.push(profile);
      }
    }),
  );

  // Sort by ID to maintain consistent order
  return profiles.sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * Clear all cached profile promises - mainly for testing purposes
 * @internal
 */
export function clearProfilePromises(): void {
  Object.keys(profilePromises).forEach((key) => {
    delete profilePromises[key];
  });
}
